import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  Input,
  OnDestroy,
  ChangeDetectorRef,
  ViewChild,
} from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CustomErrors } from '@asamco/base';
import { BehaviorSubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { LinkSettingView } from '../../interfaces/link-setting-view.interface';
import { DescriptionModalComponent } from '../description-modal/description-modal.component';
import { AsValidatorDirective } from '../../../../../../base/src/lib/as-validator/as-validator.directive';
import {
  DxSwitchModule,
  DxDateBoxModule,
  DxNumberBoxModule,
  DxLookupModule,
  DxTextBoxModule,
  DxTextAreaModule,
  DxDataGridModule,
  DxButtonModule,
  DxPopupModule,
} from 'devextreme-angular';
import { NgIf, NgSwitch, NgSwitchCase, AsyncPipe, NgFor } from '@angular/common';
import { SettingDropdownComponent } from '../setting-dropdown/setting-dropdown.component';
import { DataType } from '../../enums/data-type.enum';

// ✅ Child modal
import { FreshdeskHelpModalComponent } from '../../freshdesk-help-modal/freshdesk-help-modal.component';
import { Link } from '../../interfaces/link.interface';

export class DropDownDatasourceRow {
  Id: number;
  Value: string;
  Selected: boolean;
}

@Component({
  selector: 'al-link-edit-settings',
  templateUrl: './link-edit-settings.component.html',
  styleUrls: ['./link-edit-settings.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    NgIf,
    NgSwitch,
    NgFor,
    FormsModule,
    ReactiveFormsModule,
    NgSwitchCase,

    DxSwitchModule,
    AsValidatorDirective,
    DxDateBoxModule,
    DxNumberBoxModule,
    DxLookupModule,
    DxTextBoxModule,
    DxTextAreaModule,
    DxButtonModule,
    DxDataGridModule,
    DxPopupModule,

    SettingDropdownComponent,
    AsyncPipe,

    // ✅ add the modal component so we can use it in the template
    FreshdeskHelpModalComponent,
  ],
})
export class LinkEditSettingsComponent implements OnInit, OnDestroy {
  public customErrors: CustomErrors = {
    ERROR_REQUIRED: 'This field is required',
    ERROR_MAXLENGTH: 'This field exceeds the maximum allowed amount of characters',
  };

  public gridMapVisible = false;

  @Input() link: Link;
  @Input() setting: LinkSettingView;
  @Input() form: FormGroup;
  @Input() descriptionModal: DescriptionModalComponent;
  @Input() dateFormat: string;

  private destroy$ = new Subject<void>();

  public dataSource: any[] = [];
  public dataSourceSubject: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);

  // ✅ reference to the child modal in the template
  @ViewChild('helpModal') helpModal?: FreshdeskHelpModalComponent;

  constructor(private cdr: ChangeDetectorRef) {}

  isMapType(): boolean {
    return this.setting.dataType === DataType.Map || this.setting.dataType === DataType.EditableMap;
  }

  isMapEditable(): boolean {
    return this.setting.dataType === DataType.EditableMap;
  }

  public showGridMap(): void {
    this.gridMapVisible = true;
    this.cdr.markForCheck();
  }

  onGridSaving(event: any) {
    const changes = event.changes;
    const formArray = this.form.controls[this.setting.id];
    const currentValue = JSON.parse(formArray.value) || [];

    for (let i = 0; i < changes.length; i++) {
      const change = changes[i];

      if (change.type === 'insert') {
        currentValue.push(change.data);
      } else if (change.type === 'update') {
        for (let j = 0; j < currentValue.length; j++) {
          if (currentValue[j].Key === change.key.Key && currentValue[j].Value === change.key.Value) {
            currentValue[j] = { ...currentValue[j], ...change.data };
            break;
          }
        }
      } else if (change.type === 'remove') {
        for (let j = 0; j < currentValue.length; j++) {
          if (currentValue[j].Key === change.key.Key && currentValue[j].Value === change.key.Value) {
            currentValue.splice(j, 1);
            break;
          }
        }
      }
    }

    formArray.setValue(JSON.stringify(currentValue));
    formArray.markAsDirty();
  }

  ngOnInit(): void {
    if (this.setting.dataSource) {
      this.dataSource = JSON.parse(this.setting.dataSource);
      this.dataSourceSubject.next(this.dataSource);
    }
    if (this.setting.dataType === DataType.Map || this.setting.dataType === DataType.EditableMap) {
      this.dataSource = JSON.parse(this.setting.useValue);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  showDescription(): void {
    this.descriptionModal
      .show(this.setting)
      .pipe(takeUntil(this.destroy$))
      .subscribe(result => {
        if (result.type === 'description') {
          this.setting.settingDescription = result.content;
        } else if (result.type === 'title') {
          this.setting.displayName = result.content;
        }
        this.cdr.markForCheck();
      });
  }

  onDropdownSelectionChanged(settingValue: string) {
    this.form.controls[this.setting.id].setValue(settingValue);
    this.form.controls[this.setting.id].markAsDirty();
  }

  get freshdeskArticleIds(): number[] {
    const ids = (this.setting as any)?.freshdeskArticleIds;
    return Array.isArray(ids) ? ids : [];
  }

  // ✅ opens the child modal
 openIssueHelp(): void {
  const ids = this.freshdeskArticleIds;
  if (!ids.length) return;

  const title = this.setting?.displayName?.trim() || 'Help';

  const origin = {
    kind: 'link_settings' as const,
    linkId: this.link.id,
    setting: {
      id: this.setting?.id ?? null,
      name: (this.setting as any)?.displayName ?? null,
    },
  };

  this.helpModal?.open(title, ids, origin);
}
}
